/**
 * The rules a session is played under, and how they ride on the wire.
 *
 * Not the seed and not the seating: those describe what the world is and who is in it. These are
 * the choices a room makes about how the game treats them, and they belong to the room rather than
 * to any one machine, which is why the host owns them and every guest is told.
 *
 * A flags byte rather than a field each. There is one rule today and the second will be a bit, and
 * a byte is already the smallest thing the framing can carry.
 */

export interface SessionRules {
  /**
   * Whether a player's rounds can land on a team mate.
   *
   * Off unless a lobby turns it on together. What is here is only how the decision travels; who
   * gets to make it is [src/net/AGENTS.md](../AGENTS.md).
   */
  readonly friendlyFire: boolean;
}

export const DEFAULT_SESSION_RULES: SessionRules = { friendlyFire: false };

/** Bit positions in the rules byte. Assigned once: a bit is a wire value, not an index. */
export const RULE_BITS = { friendlyFire: 0 } as const;

export function packRules(rules: SessionRules): number {
  return rules.friendlyFire ? 1 << RULE_BITS.friendlyFire : 0;
}

/** Bits this build does not know about are ignored, so a newer host is read rather than refused. */
export function unpackRules(packed: number): SessionRules {
  return { friendlyFire: (packed & (1 << RULE_BITS.friendlyFire)) !== 0 };
}

export function rulesEqual(a: SessionRules, b: SessionRules): boolean {
  return a.friendlyFire === b.friendlyFire;
}
